'use client';

import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { formatUnits } from 'ethers';
import { usePathname } from 'next/navigation';
import { useWeb3 } from '@/contexts/Web3Context';
import { CONTRACTS } from '@/lib/web3/config';
import { getWeb3Error } from '@/lib/web3/errors';
import { TIME } from '@/lib/config/timing';
import { multicallRead } from '@/lib/web3/multicall';

const units = (value: bigint) => Number(formatUnits(value, 18));
const isTransientRpcError = (message: string) =>
  /missing revert data|could not coalesce|missing response|rpc providers are unavailable|network error|timeout|failed to fetch/i.test(
    message
  );
let activeDataLoads = 0;
const reportDataLoading = (delta: 1 | -1) => {
  activeDataLoads = Math.max(0, activeDataLoads + delta);
  window.dispatchEvent(
    new CustomEvent('aethera:data-loading', { detail: { loading: activeDataLoads > 0 } })
  );
};

export type AetheraData = {
  basic: {
    registered: boolean;
    userId: bigint;
    owner: boolean;
    sponsor: string;
    placementSide: number;
    expiry: bigint;
  };
  stake: {
    totalSP: number;
    todaySP: number;
    lastSPTime: number;
    capLimit: number;
    capUsed: number;
    available: number;
  };
  mining: {
    lastMiningClaimTime: number;
    storedPendingMining: number;
    livePendingMining: number;
    minedTokens: number;
    accountedTokenValue: number;
    portfolio: number;
  };
  network: {
    directBusiness: number;
    teamBusiness15: number;
    activeDirects: number;
  };
  binary: {
    leftQualifiedIds: number;
    rightQualifiedIds: number;
    leftBusinessCarry: number;
    rightBusinessCarry: number;
  };
  records: {
    miningTaken: number;
    appreciationTaken: number;
    networkTaken: number;
    monthlyTaken: number;
    dailyPoolTaken: number;
    weeklyPoolTaken: number;
    totalSoldUSDT: number;
    lossRestored: number;
    lapsedNetworkReward: number;
  };
  wallet: { usdt: number; aeth: number; usdtAllowance: number };
  directs: string[];
  pools: {
    dayId: bigint;
    weekId: bigint;
    monthId: bigint;
    launchTime: bigint;
    dayDuration: bigint;
    weekDuration: bigint;
    monthDuration: bigint;
    dailyBalance: number;
    weeklyBalance: number;
    monthlyBalance: number;
    dailyShare: number;
    weeklyShare: number;
    monthlyShare: number;
    dailyAchieverCount: number;
    weeklyAchieverCount: number;
    monthlyAchieverCount: number;
    dailyEligible: boolean;
    weeklyEligible: boolean;
    monthlyEligible: boolean;
    dailyQualifyingDirects: number;
    weeklyQualifyingDirects: number;
    dailyClosed: boolean;
    weeklyClosed: boolean;
    monthlyClosed: boolean;
    dailyClaimed: boolean;
    weeklyClaimed: boolean;
    monthlyClaimed: boolean;
  };
};

type AetheraDataContextValue = {
  account: string | null;
  data: AetheraData | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
};

const AetheraDataContext = createContext<AetheraDataContextValue | null>(null);
const liveDataRoutes = [
  '/dashboard',
  '/mining-rewards',
  '/network-referral-management',
  '/stake-participation-sp-management',
  '/reward-pools',
];

export function AetheraDataProvider({ children }: { children: ReactNode }) {
  const { account, miningContract, tokenContract } = useWeb3();
  const pathname = usePathname();
  const [data, setData] = useState<AetheraData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const expectedStakeTotalRef = useRef<number | null>(null);
  const dataRef = useRef<AetheraData | null>(null);
  const dataAccountRef = useRef<string | null>(null);
  const rpcRetryTimerRef = useRef<number | undefined>(undefined);
  const lastSuccessfulLoadAtRef = useRef(0);
  const refreshRunningForAccountRef = useRef<string | null | undefined>(undefined);

  const refresh = useCallback(async () => {
    // Account, route, focus and visibility effects can fire in the same render
    // cycle. One full snapshot per wallet is enough; share its eventual data.
    if (refreshRunningForAccountRef.current === account) return;
    refreshRunningForAccountRef.current = account;
    const requestId = ++requestIdRef.current;
    if (!account) {
      if (rpcRetryTimerRef.current !== undefined) window.clearTimeout(rpcRetryTimerRef.current);
      rpcRetryTimerRef.current = undefined;
      dataRef.current = null;
      dataAccountRef.current = null;
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }
    const isInitialLoad = !dataRef.current || dataAccountRef.current !== account;
    if (dataAccountRef.current && dataAccountRef.current !== account) {
      dataRef.current = null;
      setData(null);
    }
    if (isInitialLoad) {
      setLoading(true);
      reportDataLoading(1);
    }
    setError(null);
    try {
      const [contract, usdt, aeth] = await Promise.all([
        miningContract(),
        tokenContract(CONTRACTS.usdt),
        tokenContract(CONTRACTS.aethera),
      ]);
      const [
        basic,
        userId,
        stake,
        mining,
        network,
        binary,
        records,
        usdtBalance,
        aethBalance,
        allowance,
        directs,
        dayId,
        weekId,
        monthId,
        launchTime,
        dayDuration,
        weekDuration,
        monthDuration,
      ] = await multicallRead([
        { contract, method: 'getUserBasic', args: [account] },
        { contract, method: 'userIdOf', args: [account] },
        { contract, method: 'getUserStakeInfo', args: [account] },
        { contract, method: 'getUserMiningInfo', args: [account] },
        { contract, method: 'getUserNetworkInfo', args: [account] },
        { contract, method: 'getUserBinaryInfo', args: [account] },
        { contract, method: 'userRecords', args: [account] },
        { contract: usdt, method: 'balanceOf', args: [account] },
        { contract: aeth, method: 'balanceOf', args: [account] },
        { contract: usdt, method: 'allowance', args: [account, CONTRACTS.mining] },
        { contract, method: 'getDirects', args: [account] },
        // Use the contract's live period IDs for countdowns and current-pool
        // reads. The active IDs can lag after a period expires until a
        // state-changing transaction rolls the pools forward.
        { contract, method: 'currentDayId' },
        { contract, method: 'currentWeekId' },
        { contract, method: 'currentMonthId' },
        { contract, method: 'launchTime' },
        { contract, method: 'DAY' },
        { contract, method: 'WEEK' },
        { contract, method: 'MONTH' },
      ]);
      const [
        dailyBalance,
        weeklyBalance,
        monthlyOwnerBalance,
        monthlyPromoterBalance,
        finalizedDailyShare,
        finalizedWeeklyShare,
        finalizedMonthlyShare,
        dailyEligible,
        weeklyEligible,
        monthlyEligible,
        dailyCount,
        weeklyCount,
        monthlyCount,
        dailyClosed,
        weeklyClosed,
        monthlyClosed,
        dailyClaimed,
        weeklyClaimed,
        monthlyClaimed,
        dailyQualifyingDirects,
        weeklyQualifyingDirects,
      ] = await multicallRead([
        { contract, method: 'dailyPool', args: [dayId] },
        { contract, method: 'weeklyPool', args: [weekId] },
        { contract, method: 'monthlyOwnerPool', args: [monthId], allowFailure: true },
        { contract, method: 'monthlyPromoterPool', args: [monthId], allowFailure: true },
        { contract, method: 'dailyPoolShare', args: [dayId] },
        { contract, method: 'weeklyPoolShare', args: [weekId] },
        { contract, method: 'monthlyPoolShare', args: [monthId] },
        { contract, method: 'dailyAchiever', args: [dayId, account] },
        { contract, method: 'weeklyAchiever', args: [weekId, account] },
        { contract, method: 'monthlyAchiever', args: [monthId, account] },
        { contract, method: 'dailyAchieverCount', args: [dayId] },
        { contract, method: 'weeklyAchieverCount', args: [weekId] },
        { contract, method: 'monthlyAchieverCount', args: [monthId] },
        { contract, method: 'dailyPoolClosed', args: [dayId] },
        { contract, method: 'weeklyPoolClosed', args: [weekId] },
        { contract, method: 'monthlyPoolClosed', args: [monthId] },
        { contract, method: 'dailyPoolClaimed', args: [dayId, account] },
        { contract, method: 'weeklyPoolClaimed', args: [weekId, account] },
        { contract, method: 'monthlyPoolClaimed', args: [monthId, account] },
        { contract, method: 'dailyDirectAbove50Count', args: [dayId, account] },
        { contract, method: 'weeklyDirectAbove50Count', args: [weekId, account] },
      ]);
      const monthlyBalance = monthlyOwnerBalance ?? monthlyPromoterBalance;
      if (monthlyBalance === null) throw new Error('Unable to read the monthly reward pool.');
      const projectedShare = (balance: bigint, finalized: bigint, count: bigint, closed: boolean) =>
        units(closed ? finalized : count > BigInt(0) ? balance / count : BigInt(0));
      const nextData: AetheraData = {
        basic: {
          registered: basic.registered,
          userId,
          owner: basic.owner,
          sponsor: basic.sponsor,
          placementSide: Number(basic.placementSide),
          expiry: basic.expiry,
        },
        stake: {
          totalSP: units(stake.totalSP),
          todaySP: 0,
          lastSPTime: Number(stake.lastSPTime),
          capLimit: units(stake.capLimit),
          capUsed: units(stake.capUsed),
          available: units(stake.available),
        },
        mining: {
          storedPendingMining: units(mining.storedPendingMining),
          livePendingMining: units(mining.livePendingMining),
          minedTokens: units(mining.minedTokens),
          lastMiningClaimTime: Number(mining.lastMiningClaimTime),
          accountedTokenValue: units(mining.accountedTokenValue),
          portfolio: units(mining.minedTokens),
        },
        network: {
          directBusiness: units(network.directBusiness),
          teamBusiness15: units(network.teamBusiness15),
          activeDirects: Number(network.activeDirects),
        },
        binary: {
          leftQualifiedIds: Number(binary.leftQualifiedIds),
          rightQualifiedIds: Number(binary.rightQualifiedIds),
          leftBusinessCarry: units(binary.leftBusinessCarry),
          rightBusinessCarry: units(binary.rightBusinessCarry),
        },
        records: {
          miningTaken: units(records.miningTaken),
          appreciationTaken: units(records.appreciationTaken),
          networkTaken: units(records.networkTaken),
          monthlyTaken: units(records.monthlyTaken),
          dailyPoolTaken: units(records.dailyPoolTaken),
          weeklyPoolTaken: units(records.weeklyPoolTaken),
          totalSoldUSDT: units(records.totalSoldUSDT),
          lossRestored: units(records.lossRestored),
          lapsedNetworkReward: units(records.lapsedNetworkReward),
        },
        wallet: {
          usdt: units(usdtBalance),
          aeth: units(aethBalance),
          usdtAllowance: units(allowance),
        },
        directs: [...directs],
        pools: {
          dayId,
          weekId,
          monthId,
          launchTime,
          dayDuration,
          weekDuration,
          monthDuration,
          dailyBalance: units(dailyBalance),
          weeklyBalance: units(weeklyBalance),
          monthlyBalance: units(monthlyBalance),
          dailyShare: projectedShare(dailyBalance, finalizedDailyShare, dailyCount, dailyClosed),
          weeklyShare: projectedShare(
            weeklyBalance,
            finalizedWeeklyShare,
            weeklyCount,
            weeklyClosed
          ),
          monthlyShare: projectedShare(
            monthlyBalance,
            finalizedMonthlyShare,
            monthlyCount,
            monthlyClosed
          ),
          dailyAchieverCount: Number(dailyCount),
          weeklyAchieverCount: Number(weeklyCount),
          monthlyAchieverCount: Number(monthlyCount),
          dailyEligible,
          weeklyEligible,
          monthlyEligible,
          dailyQualifyingDirects: Number(dailyQualifyingDirects),
          weeklyQualifyingDirects: Number(weeklyQualifyingDirects),
          dailyClosed,
          weeklyClosed,
          monthlyClosed,
          dailyClaimed,
          weeklyClaimed,
          monthlyClaimed,
        },
      };
      // A transaction or route change may start a newer refresh while this
      // one is in flight. Never let the older snapshot overwrite it.
      if (requestId === requestIdRef.current) {
        const expectedStakeTotal = expectedStakeTotalRef.current;
        if (expectedStakeTotal !== null && nextData.stake.totalSP < expectedStakeTotal) {
          // Reject a stale RPC snapshot and retry until the confirmed stake is
          // visible from the dashboard's read provider.
          window.setTimeout(
            () => window.dispatchEvent(new Event('aethera:updated')),
            TIME.dataRefreshDelayMs
          );
        } else {
          expectedStakeTotalRef.current = null;
          if (rpcRetryTimerRef.current !== undefined) window.clearTimeout(rpcRetryTimerRef.current);
          rpcRetryTimerRef.current = undefined;
          dataRef.current = nextData;
          dataAccountRef.current = account;
          lastSuccessfulLoadAtRef.current = Date.now();
          setData(nextData);
        }
      }
    } catch (cause) {
      if (requestId === requestIdRef.current) {
        const message = getWeb3Error(cause);
        if (isTransientRpcError(message)) {
          // Public RPC nodes intermittently fail individual calls in the
          // dashboard's parallel snapshot. Preserve the last good snapshot,
          // hide low-level ethers noise, and retry after a short cooldown.
          setError(null);
          if (rpcRetryTimerRef.current === undefined) {
            rpcRetryTimerRef.current = window.setTimeout(() => {
              rpcRetryTimerRef.current = undefined;
              window.dispatchEvent(new Event('aethera:updated'));
            }, TIME.dataRetryDelayMs);
          }
        } else {
          setError(message);
        }
      }
    } finally {
      if (refreshRunningForAccountRef.current === account) {
        refreshRunningForAccountRef.current = undefined;
      }
      if (isInitialLoad) {
        if (requestId === requestIdRef.current) setLoading(false);
        reportDataLoading(-1);
      }
    }
  }, [account, miningContract, tokenContract]);

  useEffect(() => {
    const handleUpdated = () => {
      // Keep the last successful snapshot visible while confirmed on-chain
      // values are refreshed in the background.
      void refresh();
    };
    const handleStakeConfirmed = (event: Event) => {
      const expectedTotal = Number(
        (event as CustomEvent<{ expectedTotal: number }>).detail?.expectedTotal
      );
      if (Number.isFinite(expectedTotal)) expectedStakeTotalRef.current = expectedTotal;
      void refresh();
    };
    void refresh();
    window.addEventListener('aethera:updated', handleUpdated);
    window.addEventListener('aethera:stake-confirmed', handleStakeConfirmed);
    return () => {
      window.removeEventListener('aethera:updated', handleUpdated);
      window.removeEventListener('aethera:stake-confirmed', handleStakeConfirmed);
    };
  }, [refresh]);

  useEffect(() => {
    if (!account || !liveDataRoutes.some((route) => pathname.startsWith(route))) return;
    // The provider's account effect already performs the initial load. Route,
    // focus and visibility events refresh only stale data, preventing several
    // identical full snapshots from firing together during navigation.
    const refreshIfStale = () => {
      if (Date.now() - lastSuccessfulLoadAtRef.current >= TIME.priceRefreshIntervalMs) {
        void refresh();
      }
    };
    refreshIfStale();

    const refreshDashboard = () => refreshIfStale();
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') refreshIfStale();
    };
    window.addEventListener('focus', refreshDashboard);
    window.addEventListener('pageshow', refreshDashboard);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => {
      window.removeEventListener('focus', refreshDashboard);
      window.removeEventListener('pageshow', refreshDashboard);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [account, pathname, refresh]);

  useEffect(() => {
    if (pathname !== '/dashboard' || !account) return;
    const refreshDashboardData = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    const timer = window.setInterval(refreshDashboardData, TIME.dashboardDataPollIntervalMs);
    return () => window.clearInterval(timer);
  }, [account, pathname, refresh]);

  useEffect(() => {
    if (pathname !== '/dashboard' || !account) return;
    let active = true;
    let requestRunning = false;

    const refreshMiningInfo = async () => {
      if (!active || requestRunning || document.visibilityState !== 'visible') return;
      requestRunning = true;
      try {
        const contract = await miningContract();
        const mining = await contract.getUserMiningInfo(account);
        if (!active) return;
        setData((current) => {
          if (!current) return current;
          const nextData = {
            ...current,
            mining: {
              storedPendingMining: units(mining.storedPendingMining),
              livePendingMining: units(mining.livePendingMining),
              minedTokens: units(mining.minedTokens),
              lastMiningClaimTime: Number(mining.lastMiningClaimTime),
              accountedTokenValue: units(mining.accountedTokenValue),
              portfolio: units(mining.minedTokens),
            },
          };
          dataRef.current = nextData;
          return nextData;
        });
      } catch {
        // Keep the last successful live value during a temporary RPC failure.
      } finally {
        requestRunning = false;
      }
    };

    void refreshMiningInfo();
    const timer = window.setInterval(
      () => void refreshMiningInfo(),
      TIME.generatedRewardPollIntervalMs
    );
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [account, miningContract, pathname]);

  useEffect(() => {
    // Retry only when Dashboard has no successful snapshot. Once data exists,
    // this effect returns without creating an interval or another RPC call.
    if (pathname !== '/dashboard' || !account || data || loading) return;
    const retry = window.setTimeout(() => void refresh(), TIME.dataRetryDelayMs);
    return () => window.clearTimeout(retry);
  }, [account, data, loading, pathname, refresh]);

  const value = useMemo(
    () => ({ account, data, loading, error, refresh }),
    [account, data, loading, error, refresh]
  );

  return createElement(AetheraDataContext.Provider, { value }, children);
}

export function useAetheraData() {
  const value = useContext(AetheraDataContext);
  if (!value) throw new Error('useAetheraData must be used inside AetheraDataProvider.');
  return value;
}
